import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { WorkspaceStore, WorkspaceStoreState, Workspace, WorkspaceMemberRole } from '@mellify/shared';
import { localStorageWrapper } from '../lib/storage';

/**
 * Initial workspace state.
 */
const initialState: WorkspaceStoreState = {
  currentWorkspace: null,
  currentMemberRole: null,
  workspaces: [],
  isLoading: false,
  error: null,
};

/**
 * Workspace Store
 *
 * Manages workspace state including the currently active workspace
 * and the list of workspaces the user belongs to. Persists the
 * current workspace ID so it can be restored on page reload.
 *
 * @example
 * ```ts
 * const { currentWorkspace, workspaces, setCurrentWorkspace } = useWorkspaceStore();
 *
 * // With selector
 * const workspaceId = useWorkspaceStore((state) => state.currentWorkspace?.id);
 * ```
 */
export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentWorkspace: (workspace: Workspace | null) => {
        set({ currentWorkspace: workspace, currentMemberRole: null, error: null });
      },

      setCurrentMemberRole: (role: WorkspaceMemberRole | null) => {
        set({ currentMemberRole: role });
      },

      setWorkspaces: (workspaces: Workspace[]) => {
        set({ workspaces, error: null });

        // If current workspace is not in the list, reset it
        const current = get().currentWorkspace;
        if (current && !workspaces.find((w) => w.id === current.id)) {
          set({ currentWorkspace: workspaces[0] || null, currentMemberRole: null });
        }

        // If no current workspace is set, default to the first one
        if (!get().currentWorkspace && workspaces.length > 0) {
          set({ currentWorkspace: workspaces[0] });
        }
      },

      addWorkspace: (workspace: Workspace) => {
        set((state) => ({
          workspaces: [...state.workspaces, workspace],
        }));

        // If this is the first workspace, set it as current
        if (!get().currentWorkspace) {
          set({ currentWorkspace: workspace });
        }
      },

      removeWorkspace: (workspaceId: string) => {
        set((state) => {
          const filtered = state.workspaces.filter((w) => w.id !== workspaceId);
          const newState: Partial<WorkspaceStoreState> = { workspaces: filtered };

          // If the removed workspace was the current one, switch to the first available
          if (state.currentWorkspace?.id === workspaceId) {
            newState.currentWorkspace = filtered[0] || null;
            newState.currentMemberRole = null;
          }

          return newState as WorkspaceStoreState;
        });
      },

      updateWorkspace: (workspaceId: string, updates: Partial<Workspace>) => {
        set((state) => {
          const workspaces = state.workspaces.map((w) =>
            w.id === workspaceId ? { ...w, ...updates } : w
          );

          const currentWorkspace =
            state.currentWorkspace?.id === workspaceId
              ? { ...state.currentWorkspace, ...updates }
              : state.currentWorkspace;

          return { workspaces, currentWorkspace };
        });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setError: (error: string | null) => {
        set({ error });
      },
    }),
    {
      name: 'mellify-workspace',
      storage: createJSONStorage(() => localStorageWrapper),
      partialize: (state) => ({
        currentWorkspace: state.currentWorkspace,
        currentMemberRole: state.currentMemberRole,
      }),
    }
  )
);